import React, { useContext } from 'react';
import { Add, OpenInNew, Remove } from '@mui/icons-material';
import { Button, Collapse, Grid, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  gray100,
  gray90,
  primaryButtonOutlinedColor
} from '../../constants/Colors';
import { FaqType, getFaq } from '../../api/microcms/faq';

import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import { sentryLog } from '../../config/sentry';
import { AuthContext } from '../../contexts/auth';

const Faq = () => {
  const [openIndex, setOpenIndex] = useState<number | false>(false);
  const { userId, userRank, userType } = useContext(AuthContext);
  const styles = {
    title: {
      mb: '16px',
      fontWeight: 500,
      fontSize: '18px'
    },
    questionContainer: {
      flexWrap: 'nowrap',
      minHeight: '48px',
      padding: '14px 12px',
      width: '100%'
    },
    answerContainer: {
      mt: '17px',
      mb: '24px'
    },
    button: {
      fontSize: '16px',
      p: '10px 16px',
      width: '100%',
      ...primaryButtonOutlinedColor
    }
  };

  const handleClickAnswerButton = (index: number) => {
    setOpenIndex((prev) => (prev === index ? false : index));
  };

  const [contents, setContents] = useState<FaqType[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await getFaq();
        const bottomItem = response.length - 1;
        setOpenIndex(bottomItem);
        setContents(response);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        setErrorText('よくある質問の取得に失敗しました。');
        setOpen(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid sx={{ m: '24px' }}>
        <Typography sx={styles.title}>よくある質問</Typography>
        {contents?.map((item, index) => (
          <Grid
            container
            sx={{ backgroundColor: 'white', mb: '8px' }}
            key={index}
          >
            <Grid
              container
              justifyContent="space-between"
              alignItems="flex-start"
              sx={styles.questionContainer}
            >
              <Grid item>
                <Typography
                  component="span"
                  sx={{
                    mr: '8px',
                    color: gray100,
                    fontWeight: 700
                  }}
                >
                  Q.
                </Typography>
              </Grid>
              <Grid item>
                <Typography component="span" color={gray100}>
                  {item.question}
                </Typography>
              </Grid>
              <Grid flexGrow={1} />
              <Grid item>
                <IconButton
                  sx={{ width: '24px', height: '24px' }}
                  onClick={() => handleClickAnswerButton(index)}
                >
                  {openIndex === index ? <Remove /> : <Add />}
                </IconButton>
              </Grid>
            </Grid>
            <Collapse in={openIndex === index} sx={{ mr: '17px', ml: '12px' }}>
              <Grid container direction="row" sx={styles.answerContainer}>
                <Grid item>
                  <Typography
                    sx={{ mr: '8px', color: gray90, fontWeight: 500 }}
                  >
                    A.
                  </Typography>
                </Grid>
                <Grid item xs>
                  <Typography color={gray100} sx={{ overflowWrap: 'anywhere' }}>
                    {item.answer}
                  </Typography>
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        ))}
        <Button
          endIcon={<OpenInNew />}
          variant="outlined"
          sx={{ mt: '24px', mb: '16px', ...styles.button }}
          target="_blank"
          href="https://cmcos-manual.s3.ap-northeast-1.amazonaws.com/CMCOS%E7%99%BB%E9%8C%B2%E3%83%9E%E3%83%8B%E3%83%A5%E3%82%A2%E3%83%AB.pdf"
          rel="noopener noreferrer"
          onClick={() =>
            window.dataLayer.push({
              event: 'click',
              screen: window.location.pathname,
              category: 'link',
              action: 'product_save_manual',
              value: window.location.pathname.split('/').pop(),
              user_id: userId,
              user_type: userType,
              user_rank: userRank
            })
          }
        >
          <Typography sx={{ flexGrow: 1, textAlign: 'center' }}>
            登録マニュアル（使用マニュアルより抜粋）
          </Typography>
        </Button>
        <Button
          endIcon={<OpenInNew />}
          variant="outlined"
          sx={styles.button}
          onClick={() => window.open('/product/contact', '_blank')}
        >
          <Typography sx={{ flexGrow: 1 }}>
            建材登録についてお問い合わせ
          </Typography>
        </Button>
      </Grid>
      <CustomSnackbar
        autoHideDuration={6000}
        open={open}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};
export default Faq;
